// CORE module types: request/response bodies for the CORE endpoints that src/shared/api.ts does not already define.
// Owned by the CORE agent. Frontend agents import these; the endpoint table below is the contract.
//
// Every endpoint returns the documented body or an ApiError (src/shared/api.ts) with a non-2xx status.
// (A) = admin only. All non-GET calls need the `X-Requested-With: vd` header (web/src/api.ts sends it).
//
// AUTH          POST /api/auth/login {email,password} -> Me (429 after 5 failures per IP+email / 20 per IP in 15 min)
//               POST /api/auth/logout -> {ok:true}
//               GET  /api/auth/me -> Me                               POST /api/auth/bootstrap {token,email,name,password} -> Me
// USERS (A)     GET  /api/users -> UserAdmin[]                        POST /api/users UserCreateRequest -> UserAdmin
//               PATCH /api/users/:id UserPatchRequest -> UserAdmin    PUT /api/users/:id/campaigns {campaignIds} -> UserAdmin
// CAMPAIGNS     GET  /api/campaigns?status= -> Campaign[] (setters: only assigned campaigns; includes counts)
//               GET  /api/campaigns/templates -> CampaignTemplate[]   GET /api/campaigns/:id -> Campaign
//               POST /api/campaigns (A) CampaignCreateRequest -> Campaign
//               PATCH /api/campaigns/:id (A) CampaignPatchRequest -> Campaign
//               POST /api/campaigns/:id/clone (A) CampaignCloneRequest -> Campaign
//               GET  /api/campaigns/:id/users -> CampaignUser[]       PUT /api/campaigns/:id/users (A) {userIds} -> Campaign
//               POST /api/campaigns/:id/import (A) {csv, filename} -> ImportReport
//               GET  /api/campaigns/:id/imports (A) -> ImportSummary[] GET /api/campaigns/:id/imports/:importId (A) -> ImportReportStored
//               GET  /api/campaigns/:id/memberships?status=&q=&limit=&offset= (A) -> Page<MemberRow>
//               PATCH /api/campaigns/:id/memberships/:membershipId (A) MembershipPatchRequest -> MemberRow
// BUSINESSES    GET  /api/businesses?q=&campaignId=&status=&state=&limit=&offset= -> Page<BusinessListItem>   (state: 'US-OK' or 'OK')
//               GET  /api/businesses/:id -> BusinessDetail            PATCH /api/businesses/:id BusinessPatchRequest -> Business
//               POST /api/businesses/:id/verify-observation {observation?, observationSourceUrl?} -> Business
//               POST /api/businesses/:id/lookup {force?} -> Business ($0.008 Twilio Lookup; a result < 14 days old is reused unless an
//                    admin sends force:true; 429 lookup_limit after 20/h per setter, 100/h per admin)
//               POST /api/businesses/:id/dnc {reason} -> DncEntry     (adds the number to the internal DNC list, every campaign)
// QUEUE         POST /api/queue/next QueueNextRequest -> QueueNextResponseCore
//               POST /api/queue/release {membershipId} -> {ok:true}   GET /api/queue/item/:membershipId -> QueueItem
// CALLS         POST /api/calls/prepare CallPrepareRequest -> CallPrepareResponse (409 gate_blocked | disposition_required)
//               GET  /api/calls/pending -> {call: PendingCall | null}  GET /api/calls/:id -> CallState
//               POST /api/calls/:id/hangup -> {ok:true}
//               GET  /api/calls/:id/call-back -> CallBackOffer ("Call back now", 2026-10-06: the redial's gate right now;
//                    prepare it with CallPrepareRequest.quickCallback; 409 not_placed when the call never reached their line)
//               POST /api/calls/:id/disposition DispositionRequest -> DispositionResponse ('vm_dropped' is refused here;
//                    400 callback_not_allowed {details.earliestAt} when callbackAt would be a 4th call to the number in
//                    24 hours or is outside calling hours; any nearer time is fine since PJ 2026-10-21)
// TASKS         GET  /api/tasks?scope=mine|all&status=open|done&due=overdue|today|upcoming -> Task[]
//               POST /api/tasks TaskCreateRequest -> Task             PATCH /api/tasks/:id TaskPatchRequest -> Task
// MEETINGS      GET  /api/meetings/slots?membershipId=&eventSlug= -> Slot[]
//               GET  /api/meetings/questions?membershipId=&eventSlug= -> BookingQuestionsResponse
//               POST /api/meetings/book MeetingBookRequestCore -> MeetingBookResponse (409 slot_taken; 400 booking_link_refused)
//               POST /api/meetings/manual MeetingManualRequestCore -> MeetingBookResponse (400 booking_link_refused)
//               GET  /api/meetings/calendly-unlinked?callId=&membershipId=&businessId= -> CalendlyUnlinkedResponse (2026-10-20:
//                    bookings made on a Calendly link the dialer hasn't linked; 72 h, admins 30 days; the ids only steer "Likely theirs")
//               POST /api/meetings/link-calendly CalendlyLinkRequest -> MeetingBookResponse (409 already_linked | already_booked |
//                    booking_canceled | booking_missing | not_linkable; 503 calendly_unreachable)
//               GET  /api/meetings?from=&to=&status=&mine=1 -> Meeting[]
//               PATCH /api/meetings/:id {status, outcomeNote} -> Meeting
// REPORTS       GET  /api/reports/funnel?from=&to=&groupBy=week,campaign,user&campaignId=&userId= -> FunnelRow[]
//               GET  /api/reports/hours?campaignId=&from=&to= -> HourBucket[]   GET /api/reports/today -> TodayReport
// SETTINGS      GET  /api/settings -> SettingsView                   PATCH /api/settings (A) SettingsPatch -> SettingsView
//               GET  /api/settings/state-rules -> StateRule[]         PATCH /api/settings/state-rules/:code (A) StateRulePatch -> StateRule
//               GET  /api/settings/dnc?q=&limit=&offset= (A) -> Page<DncEntry>   POST /api/settings/dnc (A) {phone, reason} -> DncEntry
//               GET  /api/settings/lookups/pending?campaignId= (A) -> LookupPending
//               POST /api/settings/lookups/run (A) {campaignId?, limit?<=25} -> LookupRunResult
//               GET  /api/settings/audit?action=&userId=&entity=&entityId=&before=&limit= (A) -> AuditEntry[]
// TOOLS         Bearer ADMIN_API_TOKEN: GET /api/tools/dnc/needs-scrub (text/csv), POST /api/tools/dnc/scrub-results, GET /api/tools/export/businesses (text/csv)
//               POST /api/tools/campaigns {templateKey, name, userIds?, status?: 'draft'|'live', dryRun? (default true)} -> {dryRun, wouldCreate, goLive, existing} | 201 {dryRun:false, campaign}
//
// Dates in query strings: 'YYYY-MM-DD' (read as an OPS_TZ calendar day; `to` is inclusive) or a full ISO timestamp.

import type {
  Business, CallingWindow, Campaign, CampaignScript, Country, DispositionResponse, FunnelRow, GateReasonCode, ImportReport, ISODate, LineType,
  Meeting, Membership, MembershipStatus, PreferredWindow, QueueNextResponse, Role, Task, TaskKind, CallSummary,
  DispositionCode,
} from './api';
import type { SalesEmailSummary } from './api-sales-email';
import type { LeadNumbers, OwnerPhoneKind } from './api-numbers';
import { OWNER_PHONE_KINDS, leadNumbers } from './api-numbers';

export interface Page<T> { items: T[]; total: number }

// ---------- users ----------

export interface UserAdmin {
  id: number;
  email: string;
  name: string;
  role: Role;
  active: boolean;
  twilioIdentity: string;
  certifyExempt: boolean;
  hasPassword: boolean;
  campaignIds: number[];
  createdAt: ISODate;
  /** SPEC §19 (2026-10-02, migration 0020): this user's own @valistix.com sender for post-call emails; null = the
   *  sales_email_fallback_from setting (hello@valistix.com). Absent on servers without 0020. */
  emailFrom?: string | null;
  /** Migration 0022 (2026-10-03): the title in this user's branded email signature ("Growth Consultant"); null = the
   *  simple "<First name>" / "Valistix" sign-off. Absent on servers without 0022. */
  jobTitle?: string | null;
  /** Migration 0023 (2026-10-03): 'growth_specialist' approves their own voicemail drops on upload. Absent without 0023. */
  setterLevel?: SetterLevel;
}
export type SetterLevel = 'setter' | 'growth_specialist';
export const SETTER_LEVEL_LABELS: Record<SetterLevel, string> = { setter: 'Setter', growth_specialist: 'Growth Specialist' };
export interface UserCreateRequest {
  email: string; name: string; role: Role;
  password?: string;              // required when AUTH_MODE=password; >= 10 characters
  certifyExempt?: boolean;
  campaignIds?: number[];
}
export interface UserPatchRequest {
  name?: string; role?: Role; active?: boolean; certifyExempt?: boolean;
  password?: string;              // admin password reset (>= 10 characters); signs the user out everywhere
  campaignIds?: number[];
  emailFrom?: string | null;      // an @valistix.com address; null clears it (falls back to hello@valistix.com)
  jobTitle?: string | null;       // email signature title, up to 60 characters; null or "" clears it (simple sign-off)
  setterLevel?: SetterLevel;      // 'growth_specialist': approves their own voicemail-drop recordings
}

// ---------- campaigns ----------

export interface CampaignCreateRequest {
  templateKey?: string;           // content/campaigns template; omit to start blank (then niche, offer, country are required)
  name: string;
  geoLabel?: string | null;
  weekStart?: string | null;      // 'YYYY-MM-DD'
  country?: Country | 'MIXED';    // defaults to the template's country
  niche?: string;
  offer?: string;
  calendlyEventSlug?: string;     // fix round 2: defaults to the template's calendlyEventSlug, else 'your-result'
}
export interface CampaignPatchRequest {
  name?: string; geoLabel?: string | null; weekStart?: string | null; status?: Campaign['status'];
  maxAttempts?: number; maxVms?: number; calendlyEventSlug?: string;
  script?: CampaignScript; windows?: PreferredWindow[];
  niche?: string; offer?: string; country?: Country | 'MIXED';
}
export interface CampaignCloneRequest {
  name?: string;                  // default: '<source name> (week of <weekStart>)'
  weekStart?: string;             // default: source weekStart + 7 days
  copyOpenLeads?: boolean;        // copy memberships still new/in_progress/callback as fresh 'new' rows (default false)
}
export interface CampaignUser { id: number; name: string; email: string; role: Role; active: boolean }

export interface ImportSummary { id: number; filename: string | null; rowsTotal: number; rowsImported: number; rowsRejected: number; createdAt: ISODate; createdByName: string | null }
/** GET /api/campaigns/:id/imports/:importId: the stored copy of an ImportReport. It keeps at most 500 rejected rows
 *  (cells clipped) so it fits in D1; POST .../import returns the full list. Flags now include 'tz_ambiguous' (the
 *  city didn't settle a split time zone: the gate checks every possible zone) and 'region_mismatch' (the area code
 *  belongs to another province/state than the row says). */
export interface ImportReportStored extends ImportReport { rejectedTotal: number; rejectedStored: number }

/**
 * Lead-engine columns of a business (LEADGEN-SPEC §3.1, migration 0009), sent with every Business that
 * lib/rows.ts#businessFromRow builds. `researchAt` set = the lead engine researched it: the dialer shows the Business
 * drawer (I) and hides the pre-call Brief's Research button (§13 AI-TRAINING 5). Not in the spec-owned `Business` yet;
 * proposed in NOTES-existing.md. Read it with `leadgenFieldsOf`, which tolerates a Business without them.
 */
export interface BusinessLeadgenFields { placeId: string | null; researchAt: ISODate | null }
export function leadgenFieldsOf(b: Business): BusinessLeadgenFields {
  const x = b as Business & Partial<BusinessLeadgenFields>;
  return { placeId: x.placeId ?? null, researchAt: x.researchAt ?? null };
}

/**
 * Migration 0028 (PJ 2026-10-09): where the dialled number stands against the store's Google listing. 'store' = it
 * matches the number Google lists; 'owner_cell_likely' = Google lists a different number, so the one on the lead list
 * is probably the owner's cell (licence lists often carry it); 'unknown' = not checked or no listing found.
 */
export type PhoneKind = 'store' | 'owner_cell_likely' | 'unknown';
export const PHONE_KINDS: readonly PhoneKind[] = ['store', 'owner_cell_likely', 'unknown'];

/**
 * Columns from migration 0028, sent with every Business that lib/rows.ts#businessFromRow builds: the street address from
 * the source list (the lead card's Google Maps search fallback), the phone kind and the number Google lists for the store
 * (information only: nothing ever dials it). Read them with `listingFieldsOf`, which tolerates a Business without them.
 */
export interface BusinessListingFields {
  address: string | null; phoneKind: PhoneKind | null; googlePhoneE164: string | null;
  /** Migration 0029 (PJ 2026-10-23, src/shared/api-numbers.ts): the owner's number from the licence when phone_e164 is
   *  the store line, what the licence check said about it, and when phone_e164 alone was logged wrong. */
  ownerPhoneE164?: string | null; ownerPhoneKind?: OwnerPhoneKind | null; phoneWrongAt?: string | null;
}
export function listingFieldsOf(b: Business): BusinessListingFields {
  const x = b as Business & Partial<BusinessListingFields>;
  const kind = x.phoneKind && PHONE_KINDS.includes(x.phoneKind) ? x.phoneKind : null;
  const ownerKind = x.ownerPhoneKind && OWNER_PHONE_KINDS.includes(x.ownerPhoneKind) ? x.ownerPhoneKind : null;
  return {
    address: x.address ?? null, phoneKind: kind, googlePhoneE164: x.googlePhoneE164 ?? null,
    ownerPhoneE164: x.ownerPhoneE164 ?? null, ownerPhoneKind: ownerKind, phoneWrongAt: x.phoneWrongAt ?? null,
  };
}

/** The lead's numbers (src/shared/api-numbers.ts leadNumbers) from a Business as the API sends it. */
export function businessNumbers(b: Business): LeadNumbers {
  const f = listingFieldsOf(b);
  return leadNumbers({
    phoneE164: b.phoneE164, ownerPhoneE164: f.ownerPhoneE164, ownerPhoneKind: f.ownerPhoneKind, googlePhoneE164: f.googlePhoneE164,
    phoneKind: f.phoneKind, badNumber: b.badNumber, phoneWrongAt: f.phoneWrongAt,
  });
}

export interface BusinessLite {
  id: number; name: string; phoneE164: string; phoneDisplay: string; city: string | null; stateProv: string; country: Country;
  tz: string; lineType: LineType | null; doNotContact: boolean; badNumber: boolean; isHealthcare: boolean; sourceUrl: string;
}
export interface MemberRow extends Membership { business: BusinessLite; lockedBy: number | null; lockedUntil: ISODate | null }
export interface MembershipPatchRequest {
  status?: Extract<MembershipStatus, 'new' | 'in_progress' | 'callback' | 'closed_lost' | 'disqualified' | 'exhausted'>;
  ownerUserId?: number | null;
  clearNextEligible?: boolean;
  note?: string;                  // recorded in the audit log
}

// ---------- businesses ----------

/** One row of GET /api/businesses (the Leads page). `business.brief` is omitted (null) in lists. */
export interface BusinessListItem {
  business: Business;
  memberships: Array<Membership & { campaignName: string }>;
}
export interface BusinessDetail {
  business: Business;
  /** `campaignNiche` (2026-10-09, the Leads page's "Search the area"): absent on older servers. */
  memberships: Array<Membership & { campaignName: string; campaignNiche?: string }>;
  history: CallSummary[];         // newest first, every campaign
  /** SPEC §19 post-call email (migration 0020): emails sent to this business (subject, type, sender, time, message id). */
  emails?: SalesEmailSummary[];
  openTasks: Task[];
  meetings: Meeting[];
  onInternalDnc: boolean;
  /** Every time zone this number could be in (fix round 1). More than one = uncertain: the dial gate waits until
   *  it is calling hours in all of them. An admin settles it by setting the city or `tz` (PATCH). */
  tzCandidates: string[];
}
export interface BusinessPatchRequest {
  name?: string; niche?: string | null; phoneExt?: string | null; city?: string | null;
  website?: string | null; gbpUrl?: string | null;
  email?: string | null; emailSourceUrl?: string | null;
  dmName?: string | null; dmRole?: string | null; dmSourceUrl?: string | null;
  gatekeeperName?: string | null; bestTimeNote?: string | null;
  observation?: string | null; observationSourceUrl?: string | null;   // editing either clears the verification
  stateProv?: string; tz?: string;                                     // (A) fixes bad imports; setting tz confirms the zone
}
// PATCH /api/businesses/:id also: a Canadian business whose new name or niche looks like healthcare gets
// isHealthcare=true (never cleared) and its open leads are closed as disqualified; a new city re-infers the zone
// unless an admin confirmed it.

// ---------- queue / calls ----------

export interface QueueNextResponseCore extends QueueNextResponse {
  nextRetryAt: ISODate | null;    // earliest time a waiting or time-blocked lead becomes callable (for "next opens 9:00 am Central")
  nextOpenAt: ISODate | null;     // same value as nextRetryAt (the name FRONTEND-DIALER proposed)
  nextRetryLabel: string | null;  // e.g. 'Tue Sep 29, 3:00 PM EDT (Toronto)', prospect-local
  waitingCount: number;           // open leads resting until their next_eligible_at (4 h spacing, callbacks), not in blockedCounts
  // Migration 0006: queue/next never serves leads in hand-pick-only states (they are left out of blockedCounts and
  // waitingCount too). handPickWaiting (QueueNextResponse) counts the ones a setter could open by hand from Leads;
  // handPickStates splits that count by state, most leads first. Both are always sent by CORE.
  handPickStates?: HandPickState[];
}
/** Leads in one hand-pick-only state that a setter could open by hand, e.g. {code:'US-OK', name:'Oklahoma', count:3}. */
export interface HandPickState { code: string; name: string; count: number }
export interface PendingCall {
  id: number; membershipId: number | null; campaignId: number | null; businessId: number | null; businessName: string | null;
  status: string; startedAt: ISODate | null; toE164: string | null;
  noCampaign?: boolean;          // SPEC §19 (2026-10-02): a no-campaign pad call (membershipId null); reopen it by businessId
}
export interface CallState {
  id: number; direction: 'outbound' | 'inbound'; status: string; membershipId: number | null; businessId: number | null;
  campaignId: number | null; userId: number | null;
  preparedAt: ISODate | null; startedAt: ISODate | null; answeredAt: ISODate | null; endedAt: ISODate | null;
  durationSec: number | null; disposition: DispositionCode | null; dispositionAt: ISODate | null; vmType: 'live' | 'drop' | null;
}

// ---------- tasks ----------

export interface TaskCreateRequest {
  kind: TaskKind; dueAt: ISODate; title: string; note?: string;
  businessId?: number; membershipId?: number; campaignId?: number;
  userId?: number | null;         // admins may assign; null = any admin. Setters always create for themselves.
}
export interface TaskPatchRequest { done?: boolean; dueAt?: ISODate; note?: string | null; title?: string }

// ---------- meetings ----------

export interface MeetingBookRequestCore {
  membershipId: number; callId?: number; startTime: ISODate; name: string; email: string; mobile?: string;
  bookingSystem?: string; note?: string; textReminderOk?: boolean;
  afterFive?: string;             // the link's second select, e.g. "Who answers the phone after 5 pm today?" (optional: nothing is sent when absent)
  afterHours?: string;            // accepted alias of afterFive (what the Book panel sends)
  /** "Book into" (APPROVAL-three-links 6.2): 'your-result' | 'growth-call' | 'your-store', or the campaign's own link.
   *  Omitted = the lead's default (src/shared/calendly.ts defaultBookingSlug). Refused (400 booking_link_refused) when
   *  the lead may not book it: a cannabis lead books /your-store only, nobody else ever does. */
  eventSlug?: string;
}
// Text reminders (PJ 2026-10-11): one Calendly text ("03S Text 2h") per booking on every setter link. `textReminderOk`
// sends Calendly's text_reminder_number when the setter ticked it (the prospect gave "the best cell for a reminder
// text before the call"), a mobile was given and the booked link has a text workflow (linkSendsTexts). US, Canada and
// cannabis alike: Calendly sends it, not our Twilio (whose Text button stays off for cannabis). No morning_text task is
// created any more.
/** POST /api/meetings/manual: MeetingManualRequest plus the optional mobile, text consent and the meeting they booked. */
export interface MeetingManualRequestCore {
  /** The campaign lead; or, on a no-campaign pad call (2026-10-20), businessId instead: the meeting goes on the
   *  business's campaign lead by the Calendly webhook's rule, or on none. */
  membershipId?: number; businessId?: number;
  callId?: number; scheduledFor: ISODate; name?: string; email?: string; note?: string;
  mobile?: string; textReminderOk?: boolean;
  eventSlug?: string;             // "Which meeting did they book?": same rule as MeetingBookRequestCore.eventSlug
}

// ---------- Calendly bookings made on the link (owner request, PJ, 2026-10-20) ----------
// PJ: "sometimes when we're on the calls, we like to book meetings by doing it through the Calendly links and not
// through the dialer ... there's an option to mark it if you booked it through the Calendly link or to book it through
// the dialer itself." The Calendly webhook links most bookings by itself; the rest (no single business matched) are
// listed here and linked by hand.

/** One booking the dialer hasn't linked to a business (an unmatched or failed invitee.created). */
export interface CalendlyUnlinkedBooking {
  inviteeUri: string;
  receivedAt: ISODate;            // when Calendly told us: about when they booked
  /** 'matched' (review 2026-10-20): the dialer already matched it to this call's business by itself and recorded the
   *  meeting with no call; listed only for that call, and Link ties it to the call (meetingId is that meeting). */
  result: 'unmatched' | 'error' | 'matched';
  meetingId: number | null;
  inviteeName: string | null;
  inviteeEmail: string | null;
  textNumber: string | null;      // the cell they gave Calendly for its reminder text
  timezone: string | null;        // theirs, as Calendly saw it
  businessAnswer: string | null;  // their answer to "Business name"
  startTime: ISODate | null;      // the meeting
  slug: string | null;            // the link booked (null when Calendly can't say)
  linkName: string;               // bookingLinkName(slug), or Calendly's event name
  answers: Array<{ question: string; answer: string }>;
  /** Booked while this call was live or in the 30 min after it, or their email / phone / business name fits this lead. */
  likely: boolean;
  likelyWhy: string[];            // plain words, e.g. "Booked during this call", "Same email"
  /** Only the links are known: the webhook kept no copy and Calendly couldn't be asked. */
  detailsMissing: boolean;
  /** The businesses the webhook weighed and couldn't choose between (e.g. two with the same name). */
  candidates: Array<{ id: number; name: string }>;
}
export interface CalendlyUnlinkedResponse {
  bookings: CalendlyUnlinkedBooking[];   // likely first, then newest first
  windowHours: number;                   // 72, or 720 for an admin
  calendlyConnected: boolean;
}
/** POST /api/meetings/link-calendly. The lead: membershipId (the dialer's campaign lead); or businessId (a no-campaign
 *  pad call, or an admin's pick on the Meetings page: the business's campaign lead by the webhook's rule, or none); or
 *  only the callId (its lead, or its business). setterId: an admin linking with no call may give the credit to someone
 *  (default: the webhook's rules); with a call, the setter on the call gets it. */
export interface CalendlyLinkRequest {
  inviteeUri: string;
  callId?: number;
  membershipId?: number;
  businessId?: number;
  setterId?: number;
}
/** GET /api/meetings/questions?membershipId=&eventSlug= and GET /api/meetings/slots take the same optional eventSlug.
 *  The questions answer also carries the links this lead may book (the server's view: it knows every campaign the
 *  business is in, so it can lock a lead the panel thought was not cannabis). */
export interface BookingLinkOptionsView { slugs: string[]; defaultSlug: string; locked: boolean }
export interface BookingQuestionsResponse {
  slug: string;
  primary: { name: string; choices: string[] } | null;
  afterFive: { name: string; choices: string[] } | null;
  options?: BookingLinkOptionsView;
}
export interface MeetingBookResponse extends Meeting {
  tasksCreated: Task[];
  disposition: DispositionResponse | null;   // set when callId was given
}
/** Calendly setter-link question 3 choices (CALENDLY.md §2 booking form). */
export const BOOKING_SYSTEM_CHOICES = [
  'Housecall Pro or Jobber', 'ServiceTitan', 'A clinic, salon or studio system', 'Google Calendar, paper or a whiteboard', 'Something else',
] as const;
/** Calendly question 4 choices. */
export const AFTER_FIVE_CHOICES = ['We do, from our cells', 'An answering service', 'Voicemail', 'Nobody, it just rings'] as const;

// ---------- reports ----------

/** GET /api/reports/funnel rows carry the no-show count too, so totals can recompute the show rate exactly. */
export interface FunnelRowCore extends FunnelRow { meetingsNoShow: number }

export interface LeaderboardRow { userId: number; name: string; dials: number; connects: number; dmConversations: number; meetings: number }
export interface TodayReport { mine: FunnelRowCore; leaderboard: LeaderboardRow[] }

// ---------- settings ----------

/** National DNC scrub scope (US). 'off' (owner decision 2026-10-01): the national registry is not used, so a missing
 *  or stale scrub never blocks a call and a state rule's scrubAllLines is ignored. Our internal DNC list always applies. */
export type DncScrubScope = 'mobile_unknown' | 'all_us' | 'off';
export const DNC_SCRUB_SCOPES: readonly DncScrubScope[] = ['mobile_unknown', 'all_us', 'off'];
/** Call recording (SPEC §19, 2026-10-01). 'all': every connected call is recorded. 'one_party_only' (default): calls with
 *  a business in a state whose rule has allPartyConsent (or a California/Washington area code, or an unknown US state)
 *  are not recorded; everything else is. Recordings are filed privately in R2 and served by no route. */
export type RecordingMode = 'all' | 'one_party_only';
export const RECORDING_MODES: readonly RecordingMode[] = ['all', 'one_party_only'];

export interface SettingsView {
  callingWindows: { US: CallingWindow; CA: CallingWindow };
  blockedDates: { US: string[]; CA: string[] };
  dncScrubScope: DncScrubScope;
  /** Retired 2026-10-01 (REVIEW-2026-10-01): the server no longer sends it ("not interested" is a 90-day cooldown, not
   *  DNC), so the Settings toggle that reads it stays hidden. Kept optional only so older screens still compile. */
  notInterestedIsDnc?: boolean;
  recordingMode: RecordingMode;
  mainNumberId: number | null;
  mainNumberE164: string | null;  // the {callback} number for voicemail scripts
  inboundGreeting: string;
  legalName: string;              // mirrors env LEGAL_NAME (read-only)
  /** Postal address for every commercial email ({mailing_address}; CASL s.6(2), CAN-SPAM). null = not set: every
   *  email button must stay disabled ("Add the company mailing address in Settings first (CASL / CAN-SPAM)"). */
  mailingAddress: string | null;
  /** SPEC §19 lead recycling (migration 0018). Absent on servers without it (the Settings card hides). */
  recycle?: RecycleSettings;
  /** SPEC §19 post-call email (migration 0020): the sender for users without their own email_from (an @valistix.com
   *  address, default hello@valistix.com), and whether each email is BCC'd to the sender's own address (default on). */
  salesEmailFallbackFrom?: string;
  salesEmailBccSender?: boolean;
  /** Which vendor transcribes a call recording for an email draft first; the other is the automatic fallback. */
  transcriptionVendor?: 'cartesia' | 'gemini';
  /** Texting (2026-10-03): the carriers approved our US A2P 10DLC registration. Default false: US texts are pending and
   *  the composer says so. PATCH { smsUsA2pApproved: true } once Twilio shows the campaign as approved. */
  smsUsA2pApproved?: boolean;
  /** PJ 2026-10-07: false (default) = setters don't see admins' dials, connects, meetings or pace, and no team total they
   *  see includes them; true = they see them like anyone else's. PATCH { adminNumbersVisible } (admins only). */
  adminNumbersVisible?: boolean;
  /** Booked calls → Discord (migration 0027). 'set' = the Worker secret DISCORD_MEETINGS_WEBHOOK_URL holds a Discord webhook
   *  URL (the URL itself is never sent); 'invalid' = it holds something else, so nothing is posted. */
  discordWebhook?: 'set' | 'missing' | 'invalid';
  /** What Discord last answered for the current URL (a fingerprint of it is kept, never the URL): 'untested' (a new or
   *  changed secret: press Send test post), 'ok' (a post or a test post went through), 'failing' (Discord said the webhook
   *  is gone or the token is wrong: every post is paused until the URL changes or a test post succeeds). */
  discordWebhookHealth?: { state: 'untested' | 'ok' | 'failing'; at: string | null; error: string | null };
  /** Booked meetings whose Discord post failed: still retrying / paused (failing) and out of attempts (gaveUp). */
  discordFailedPosts?: { failing: number; gaveUp: number };
  /** Post each booked meeting to Discord (default on once the URL is set). */
  discordMeetingsEnabled?: boolean;
  /** Attach the call recording (under 8 MB) to the Discord post (default on). */
  discordAttachAudio?: boolean;
  /** PJ 2026-10-20 (setting 'consent_recording_enabled', default false): on a connected outbound call the recording rule
   *  skipped for all-party consent (a US all-party state, or a California or Washington area code), the call bar offers
   *  "Record (they said yes)": the setter asks, and only after their yes starts the recording for the rest of the call. */
  consentRecordingEnabled?: boolean;
}
/** Automatic lead recycling (SPEC §19, 2026-10-02). Out of attempts with no decision-maker reached: rest exhaustedDays,
 *  then one extra round of extraAttempts. "Has one already": back after hasOneDays for a fresh round. */
export interface RecycleSettings {
  enabled: boolean;
  exhaustedDays: number;   // 7-365, default 30
  extraAttempts: number;   // 1-6, default 3
  hasOneDays: number;      // 30-730, default 180
}
export interface SettingsPatch {
  callingWindows?: { US: CallingWindow; CA: CallingWindow };   // must sit 1 h inside the legal hours (see settings.ts ADMIN_WINDOW_BOUNDS); sunday: US only, CA must be null/absent
  blockedDates?: { US: string[]; CA: string[] };
  dncScrubScope?: DncScrubScope;
  /** Retired 2026-10-01: PATCH answers 400. */
  notInterestedIsDnc?: boolean;
  recordingMode?: RecordingMode;
  mainNumberId?: number | null;
  inboundGreeting?: string;
  mailingAddress?: string | null; // a real postal address (street or PO box, city, province/state, postal code); null clears it
  recycle?: Partial<RecycleSettings>;
  salesEmailFallbackFrom?: string; // an @valistix.com address
  salesEmailBccSender?: boolean;
  transcriptionVendor?: 'cartesia' | 'gemini';
  smsUsA2pApproved?: boolean;     // texting: true once Twilio shows the US A2P 10DLC campaign approved
  adminNumbersVisible?: boolean;  // true = setters see admins' activity numbers like anyone else's (default false)
  discordMeetingsEnabled?: boolean; // booked calls: post each booked meeting to Discord
  discordAttachAudio?: boolean;     // booked calls: attach the recording (under 8 MB) to the post
  consentRecordingEnabled?: boolean; // PJ 2026-10-20: offer "Record (they said yes)" on all-party-consent calls (default false)
}
export interface StateRulePatch {
  callingAllowed?: boolean;
  dropAllowed?: boolean;
  window?: CallingWindow | null;
  registrationNote?: string | null;
  notes?: string | null;          // required (a real review note) when turning callingAllowed/dropAllowed on for a US state
  sources?: string[];             // >= 1 http(s) URL required for the same
  markReviewed?: boolean;         // sets reviewedAt = now (US: needs notes + sources)
  // Fix round 2 (migration 0005). Turning either condition OFF on an open US state needs notes + sources like opening it.
  scrubAllLines?: boolean;        // national DNC scrub for every line type in this state
  businessLinesOnly?: boolean;    // dial only lookup-verified (<= 90 days) landline / fixedVoip numbers ('state_line_type')
  setterNote?: string | null;     // <= 400 chars, shown to the setter as QueueItem.stateNote; null or '' clears it
  // Migration 0006. Turning it OFF on an open US state needs notes + sources like the other conditions.
  handPickOnly?: boolean;         // never auto-selected by queue/next; the setter opens each lead by hand (Leads → Call)
  // Migration 0021 (SPEC §19). Turning it OFF on an open US state needs notes + sources like the other conditions.
  noSunday?: boolean;             // state restricts telemarketing on Sundays: no Sunday calls here, whatever the US Sunday hours
}
export interface DncEntry { phoneE164: string; reason: string | null; source: 'call_request' | 'inbound_optout' | 'manual' | 'import' | 'email_optout'; businessId: number | null; businessName: string | null; createdByName: string | null; createdAt: ISODate }
export interface LookupPending { pending: number; estimatedCostUsd: number }
export interface LookupRunResult {
  processed: number; updated: number; remaining: number; costUsd: number;
  failed: Array<{ businessId: number; name: string; error: string }>;   // failures are recorded and skipped for 24 h
}
export interface AuditEntry { id: number; userId: number | null; userName: string | null; action: string; entity: string | null; entityId: string | null; detail: unknown; createdAt: ISODate }

// ---------- gate helpers shared with the UI ----------

/** Reasons that clear on their own with time (retryAt is only given when every failing reason is one of these). */
export const TIME_BASED_REASONS: readonly GateReasonCode[] = ['outside_window', 'sunday', 'blocked_date', 'attempt_spacing', 'attempts_24h', 'attempts_day_limit'];

// ---------- Canadian healthcare detection (SPEC §5.4, shared with the UI) ----------
// The worker's importer, campaign checks, business edits and dial gate all use this (via src/worker/lib/healthcare.ts);
// the admin UI should use it too so its warnings match the server. It fails closed. Terms common in cannabis-store
// names ("therapy", "wellness", "apothecary", "medicine") are left out on purpose; the specific therapies are listed.

/** SPEC §5.4 list, widened after fix round 1 (physicians, denturists, psychotherapists, osteopaths, midwives, ...). */
export const HEALTHCARE_RE = new RegExp([
  // physiotherapy, chiropractic, massage
  'physio', 'physiotherap', 'physical therap', 'chiropract', 'chiro\\b', 'chiropod', 'massage therap', '\\bRMT\\b',
  // dental
  'dental', 'dentist', 'dentur', '(ortho|perio|endo)dont', 'hygienist',
  // doctors, clinics, hospitals
  'medical', 'clinic', 'health ?care', 'health (cent(re|er)|services?)', 'hospital', 'hospice', 'physician',
  '(?<!(drain|lawn|computer|phone|pc|tree|roof|carpet|pool|car|auto|appliance) )doctors?\\b', '\\bdr\\.?\\s',
  'surg(eon|ery|ical)', 'fertility', 'radiolog', 'long[- ]term care', 'nurs(e|es|ing)\\b', 'midwi(fe|ves|fery)',
  'med(i)?[- ]?spa',
  // eyes, ears, feet, pharmacy
  'optom', 'optic', 'ophthalm', 'audiolog', 'hearing', 'podiat', 'pedorth', 'orthotic', 'prosthet', 'pharmac',
  // rehab and therapies
  'rehab', 'occupational therap', 'speech[- ]?(therap|language|patholog)', 'respiratory therap', 'therapist',
  'kinesiolog', 'osteopath', 'dietitian',
  // mental health
  'psycholog', 'psychiatr', 'psychotherap', 'mental health', 'counsel+(ing|ors?)\\b',
  // complementary
  'acupunct', 'naturopath', 'homeopath',
  // animals (kept from the SPEC list)
  'veterinar',
].join('|'), 'i');

export function looksHealthcare(...texts: Array<string | null | undefined>): boolean {
  return texts.some((t) => !!t && HEALTHCARE_RE.test(t));
}
